<template>
    <PainelGrafico
        :titulo="tituloPainel"
        v-model:aberto="mostrarGrafico"
        :carregando="carregando"
        :vazio="!carregando && !temDados"
        :dica="mes == null ? 'Clique em um mês para ver as semanas' : ''"
    >
        <template #info>
            <v-menu location="end">
                <template v-slot:activator="{ props }">
                    <i v-bind="props" class="bi bi-info-circle" title="Sobre o indicador"></i>
                </template>
                <v-card min-width="100" style="max-width: 24rem; padding: 1rem">
                    <div>
                        <b>On Time:</b> % com aviso de conclusão até a data de entrega pactuada.<br>
                        <b>In Full:</b> % com quantidade e itens completos (ainda indisponível).<br>
                        <b>Polaridade:</b> Quanto maior, melhor (meta {{ meta }}%).<br>
                        <b>Fonte:</b> Propostas comerciais (data pactuada × aviso de conclusão).
                    </div>
                </v-card>
            </v-menu>
        </template>

        <template #filtros>
            <div class="modo-seletor" role="tablist" aria-label="Visualização dos componentes">
                <button
                    v-for="opcao in modos"
                    :key="opcao.id"
                    type="button"
                    role="tab"
                    :aria-selected="modo === opcao.id"
                    :aria-disabled="opcao.desabilitado"
                    :disabled="opcao.desabilitado"
                    :title="opcao.desabilitado ? motivoInFull : opcao.label"
                    :class="{ ativo: modo === opcao.id, desabilitado: opcao.desabilitado }"
                    @click="setModo(opcao)"
                >
                    {{ opcao.label }}
                </button>
            </div>
        </template>

        <Bar
            :key="modo"
            :options="dadosGrafico().options"
            :data="dadosGrafico().data"
            ref="barChart"
        />
    </PainelGrafico>
</template>

<script>
import serviceGraficos from '@/services/serviceGraficos.js';
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
} from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';
import PainelGrafico from '@/components/PainelGrafico.vue';
import { getChartTheme, baseChartOptions } from '@/utils/chartTheme';

ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement
);

export default {
    name: 'OtifComponentes',
    components: { Bar, PainelGrafico },
    props: {
        mes: { required: true },
        ano: { required: true },
        modoInicial: {
            type: String,
            default: 'onTime',
            validator: (v) => ['comparativo', 'onTime', 'inFull'].includes(v),
        },
        motivoInFull: {
            type: String,
            default: 'Dados de quantidade entregue ainda indisponíveis',
        },
    },
    emits: ['updateMes', 'update:modo'],
    data() {
        return {
            data: null,
            meta: 95,
            mostrarGrafico: true,
            carregando: true,
            modo: this.modoInicial === 'inFull' || this.modoInicial === 'comparativo'
                ? 'onTime'
                : this.modoInicial,
            modos: [
                { id: 'comparativo', label: 'Comparativo', desabilitado: true },
                { id: 'onTime', label: 'On Time', desabilitado: false },
                { id: 'inFull', label: 'In Full', desabilitado: true },
            ],
        };
    },
    computed: {
        tituloPainel() {
            return 'On Time';
        },
        temDados() {
            return Boolean(this.data?.onTime?.length);
        },
    },
    watch: {
        mes: 'getDados',
        ano: 'getDados',
        modoInicial(novo) {
            if (novo === 'onTime' && novo !== this.modo) {
                this.modo = novo;
            }
        },
        modo(novo) {
            this.$emit('update:modo', novo);
        },
    },
    mounted() {
        this.getDados();
    },
    methods: {
        setModo(opcao) {
            if (opcao.desabilitado) return;
            this.modo = opcao.id;
        },
        async getDados() {
            this.carregando = true;
            try {
                this.data = await serviceGraficos.getOtif(this.mes, this.ano);
                this.meta = this.data.meta;
            } catch (error) {
                console.error('Erro ao obter componentes On Time:', error);
                this.data = null;
            } finally {
                this.carregando = false;
            }
        },
        onClickBarra(e) {
            if (this.mes != null || !this.$refs.barChart?.chart) return;
            const canvasPosition = getRelativePosition(e, this.$refs.barChart.chart);
            const dataX = this.$refs.barChart.chart.scales.x.getValueForPixel(canvasPosition.x);
            if (dataX == null || dataX < 0) return;
            const ponto = this.data?.onTime?.[dataX];
            if (ponto?.mes) {
                this.$emit('updateMes', ponto.mes);
            }
        },
        dadosGrafico() {
            const theme = getChartTheme();
            const options = baseChartOptions({
                ...theme,
                suffix: '%',
                onClick: this.onClickBarra,
            });
            options.scales.y.min = 0;
            options.scales.y.max = 100;
            options.scales.y.ticks.callback = (value) => `${value}%`;

            return {
                data: {
                    datasets: [
                        {
                            label: 'On Time %',
                            backgroundColor: '#3571CD',
                            borderRadius: 4,
                            data: this.data?.onTime || [],
                        },
                        {
                            label: 'Meta %',
                            type: 'line',
                            borderColor: '#E1BB22',
                            backgroundColor: '#E1BB22',
                            borderWidth: 2,
                            pointRadius: 0,
                            tension: 0,
                            data: this.data?.metaSerie || [],
                        },
                    ],
                },
                options,
            };
        },
    },
};
</script>

<style scoped>
.modo-seletor {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    padding: 0.25rem;
    border-radius: 10px;
    background: color-mix(in srgb, #5b4fcf 8%, var(--cor-bg));
    box-shadow: 0 0 0 1px color-mix(in srgb, #5b4fcf 18%, transparent) inset;
}

.modo-seletor button {
    border: none;
    border-radius: 8px;
    padding: 0.4rem 0.75rem;
    background: transparent;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 600;
    color: inherit;
    opacity: 0.7;
    transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.modo-seletor button:hover:not(:disabled) {
    opacity: 1;
    background: color-mix(in srgb, #5b4fcf 12%, transparent);
}

.modo-seletor button.ativo {
    opacity: 1;
    background: #5b4fcf;
    color: #fff;
}

.modo-seletor button.desabilitado,
.modo-seletor button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    text-decoration: line-through;
}
</style>
